import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { Link } from 'react-router-dom';

import { getItem } from '../../services/items';
import { confirmItem } from '../../services/confirmations';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { normalizeId } from '../../utils/ids';
import {
	CHECK_ANSWERS,
	FIXED,
	NOT_FIXED,
	STEP_KIND,
	advancePath,
	answerStep,
	backPath,
	canGoBack,
	resolveNext,
	startPath,
	stepKind,
	stepTitle,
	walkState,
} from '../../utils/troubleshooting';
import MarkdownContent from '../common/MarkdownContent';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { choiceListOf } from '../../utils/choiceValues';
import ExternalLink from '../common/ExternalLink';

const text = value => (typeof value === 'string' ? value.trim() : '');

const isGone = error => {
	const status = error?.response?.status;
	return status === 404 || status === 403;
};

function choiceLabel(def, value) {
	// A Choice's list only (KPE-033): another type's list is inert.
	const found = choiceListOf(def).find(choice => choice?.value === value);
	return found?.label || value || '';
}

function Detail({ label, children }) {
	if (!children) return null;
	return (
		<div className="troubleshoot-walk__detail">
			<div className="troubleshoot-walk__label">{label}</div>
			<div>{children}</div>
		</div>
	);
}

function CheckStep({ node, onAnswer }) {
	const e = node.elements || {};
	return (
		<>
			<h3 className="troubleshoot-walk__question">{stepTitle(node, STEP_KIND.CHECK)}</h3>
			<MarkdownContent markdown={e['how-to-check']} className="troubleshoot-walk__body" />
			<div className="troubleshoot-walk__answers" role="group" aria-label="Your answer">
				{CHECK_ANSWERS.map(answer => (
					<Button key={answer.value} className="troubleshoot-walk__answer" onClick={() => onAnswer(answer.value)}>
						{answer.label}
					</Button>
				))}
			</div>
		</>
	);
}

function FixStep({ node, collectionId, onAnswer }) {
	const e = node.elements || {};
	const howTo = normalizeId(Array.isArray(e['how-to']) ? e['how-to'][0] : e['how-to']);
	return (
		<>
			<h3 className="troubleshoot-walk__question">{stepTitle(node, STEP_KIND.FIX)}</h3>
			<MarkdownContent markdown={e['do-this']} className="troubleshoot-walk__body" />
			{howTo && collectionId ? (
				<p>
					<Link to={`/collections/${collectionId}/items/${howTo}`}>Open the linked guide</Link>
				</p>
			) : null}
			<h4 className="troubleshoot-walk__ask">Did this fix it?</h4>
			<div className="troubleshoot-walk__answers" role="group" aria-label="Did this fix it?">
				<Button className="troubleshoot-walk__answer" onClick={() => onAnswer(FIXED)}>Yes</Button>
				<Button className="troubleshoot-walk__answer" onClick={() => onAnswer(NOT_FIXED)}>No</Button>
			</div>
		</>
	);
}

function HelpStep({ node, urgencyDef }) {
	const e = node.elements || {};
	const contact = text(e.contact);
	const phone = text(e.phone);
	const safeContact = /^https?:\/\//i.test(contact) ? contact : '';
	return (
		<>
			<h3 className="troubleshoot-walk__question">Get help</h3>
			<Detail label="Who to contact">{text(e.who)}</Detail>
			<Detail label="How soon">{choiceLabel(urgencyDef, e.urgency)}</Detail>
			<Detail label="Where">
				{safeContact ? <ExternalLink href={safeContact}>{contact}</ExternalLink> : null}
			</Detail>
			<Detail label="Phone">
				{phone ? <a href={`tel:${phone.replace(/[^\d+]/g, '')}`}>{phone}</a> : null}
			</Detail>
			<Detail label="Have this ready">
				{text(e['have-ready']) ? <span className="troubleshoot-walk__ready">{text(e['have-ready'])}</span> : null}
			</Detail>
			<Detail label="More">
				{text(e.details) ? <MarkdownContent markdown={e.details} className="troubleshoot-walk__body" /> : null}
			</Detail>
		</>
	);
}

function OtherStep({ node }) {
	return (
		<>
			<h3 className="troubleshoot-walk__question">{stepTitle(node, STEP_KIND.OTHER)}</h3>
			<MarkdownContent markdown={node.elements?.details} className="troubleshoot-walk__body" />
			<p className="troubleshoot-walk__note">This step is not a check, a fix or a way to get help, so the walk-through stops here.</p>
		</>
	);
}

/**
 * Guided troubleshooting (KPR-491): walks a Symptom's diagnostic tree one step
 * at a time. Start from the symptom's `start-at`; a Check shows its question
 * with Yes, No and Not sure; a Fix shows its steps and asks "Did this fix it?"
 * (Yes counts as a confirmation on the Fix's `fixed-it`, when it has one); a
 * Get help shows who to contact and what to have ready. Back returns step by
 * step and keeps the path; a branch that is not set, a loop and a step that
 * can't be read all end on a plain message, never on a broken screen.
 *
 * The pure rules are in utils/troubleshooting.js. Nothing is saved: the path
 * lives in this dialog only.
 */
export default function TroubleshootWalkthrough({ open, onClose, symptom, title, collectionId }) {
	const cards = useOptionalCardDefinitions();
	const cardsById = cards?.cardsById;
	const getElementsForCard = cards?.getElementsForCard;

	const [path, setPath] = useState([]);
	// id -> item, or null when it could not be read (deleted, or not visible).
	const [nodes, setNodes] = useState({});
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const [counted, setCounted] = useState(false);
	const tried = useRef(new Set());

	const symptomId = normalizeId(symptom?._id);
	const start = useMemo(() => startPath(symptom), [symptom]);

	// A new walk each time the dialog opens (or is opened on another symptom).
	useEffect(() => {
		if (!open) return;
		setPath(start);
		setNodes({});
		setFailed(false);
		setCounted(false);
		tried.current = new Set();
	}, [open, symptomId, start]);

	const state = walkState(path, nodes);
	const currentId = path.length ? path[path.length - 1].id : null;
	const needsFetch = open && currentId && !(currentId in nodes);

	useEffect(() => {
		if (!needsFetch) return undefined;
		let active = true;
		setFailed(false);
		getItem(currentId)
			.then(item => { if (active) setNodes(prev => ({ ...prev, [currentId]: item })); })
			.catch(error => {
				if (!active) return;
				if (isGone(error)) setNodes(prev => ({ ...prev, [currentId]: null }));
				else setFailed(true);
			});
		return () => { active = false; };
	}, [needsFetch, currentId, attempt]);

	// A failed read belongs to the step it was for; Back to a step already read clears it.
	useEffect(() => { setFailed(false); }, [currentId]);

	const kindFor = useCallback(item => stepKind(item, cardsById?.[normalizeId(item?.card_id)]), [cardsById]);
	const node = state.phase === 'step' ? nodes[state.id] : null;
	const kind = node ? kindFor(node) : null;

	const choose = useCallback(answer => {
		const current = nodes[currentId];
		if (answer === FIXED) {
			setPath(prev => answerStep(prev, FIXED));
			const hasConfirmation = (getElementsForCard?.(current?.card_id) || [])
				.some(def => def?.name === 'fixed-it' && def?.dataType === 'confirmations');
			if (hasConfirmation && !tried.current.has(currentId)) {
				tried.current.add(currentId);
				// Counted when the API allows it; a refusal changes nothing the person sees.
				confirmItem(currentId, 'fixed-it').then(() => setCounted(true)).catch(() => {});
			}
			return;
		}
		setPath(prev => {
			const resolved = resolveNext(current, answer, prev);
			return resolved.next ? advancePath(prev, answer, resolved.next) : answerStep(prev, answer);
		});
	}, [nodes, currentId, getElementsForCard]);

	const restart = () => { setPath(start); setCounted(false); };

	const urgencyDef = useMemo(
		() => (getElementsForCard?.(node?.card_id) || []).find(def => def?.name === 'urgency'),
		[getElementsForCard, node],
	);

	// The steps already walked, each with the answer given there.
	const past = path.slice(0, -1).concat(path.length && path[path.length - 1].answer !== null ? [path[path.length - 1]] : []);

	const startMissing = open && !start.length;
	const loaded = startMissing || state.phase !== 'step' || Boolean(node);

	let body = null;
	if (startMissing) {
		body = <p className="troubleshoot-walk__note">{state.message}</p>;
	} else if (state.phase === 'end') {
		body = <p className="troubleshoot-walk__note" role="status">{state.message}</p>;
	} else if (state.phase === 'fixed') {
		body = (
			<p className="troubleshoot-walk__note" role="status">
				Glad that fixed it.{counted ? ' Your answer was counted on this fix.' : ''}
			</p>
		);
	} else if (node && kind === STEP_KIND.CHECK) {
		body = <CheckStep node={node} onAnswer={choose} />;
	} else if (node && kind === STEP_KIND.FIX) {
		body = <FixStep node={node} collectionId={collectionId} onAnswer={choose} />;
	} else if (node && kind === STEP_KIND.HELP) {
		body = <HelpStep node={node} urgencyDef={urgencyDef} />;
	} else if (node) {
		body = <OtherStep node={node} />;
	}

	const ended = state.phase === 'end' || state.phase === 'fixed';
	const symptomText = text(symptom?.elements?.['what-you-notice']);

	return (
		<Modal
			title={title ? `Troubleshoot: ${title}` : 'Troubleshoot'}
			open={open}
			onCancel={onClose}
			className="troubleshoot-walk"
			footer={(
				<div className="troubleshoot-walk__footer">
					<Button className="troubleshoot-walk__control" disabled={!canGoBack(path)} onClick={() => setPath(prev => backPath(prev))}>
						Back
					</Button>
					{ended && start.length ? (
						<Button className="troubleshoot-walk__control" onClick={restart}>Start over</Button>
					) : null}
					<Button className="troubleshoot-walk__control" onClick={onClose}>Close</Button>
				</div>
			)}
		>
			<DialogLoadState
				loaded={loaded}
				loading={false}
				error={failed}
				label="this step"
				onRetry={() => { setFailed(false); setAttempt(n => n + 1); }}
				skeleton={<Skeleton variant="list-row" count={3} />}
				minHeight={200}
			>
				<div className="troubleshoot-walk__panel">
					{symptomText ? <p className="troubleshoot-walk__symptom">{symptomText}</p> : null}
					{past.length ? (
						<ol className="troubleshoot-walk__path" aria-label="Your path so far">
							{past.map(step => {
								const item = nodes[step.id];
								const label = item ? stepTitle(item, kindFor(item)) : 'A step that is not available';
								return (
									<li key={step.id}>
										{label}
										{step.answer ? <span className="troubleshoot-walk__given">{` ${answerWord(step.answer)}`}</span> : null}
									</li>
								);
							})}
						</ol>
					) : null}
					{body}
				</div>
			</DialogLoadState>
		</Modal>
	);
}

function answerWord(answer) {
	if (answer === 'yes') return 'Yes';
	if (answer === 'no') return 'No';
	if (answer === 'unsure') return 'Not sure';
	if (answer === FIXED) return 'Fixed it';
	if (answer === NOT_FIXED) return 'Did not fix it';
	return '';
}
